import { Injectable, computed, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import {
    DynamicContent,
    FieldMerge,
    FieldMergeGroup,
} from '@r1-compass-library/models/content-library/content-library.model';
import { Observable, defaultIfEmpty, exhaustMap, map, of } from 'rxjs';
import { ContentAssetService, DataMartService } from '@r1-compass-library/services';
import { mapArray } from 'ngxtension/map-array';
import {
    ContentAsset,
    DataMartMergeField,
    DataMartMergeFieldResponse,
    DataMartTable,
} from '@r1-compass-library/models';
import { ContentAssetSearch } from '@r1-compass-library/models/search/content-asset-search.model';
import { exhaustOp, rxMutation } from '@angular-architects/ngrx-toolkit';

export interface DynamicContentState {
    mergeFieldTables: Pick<DataMartTable, 'displayName' | 'name'>[];
    dynamicContent: DynamicContent[];
    mergeFields: DataMartMergeField[];
    libraryContent: ContentAsset[];
    deletedLibraryContent: ContentAsset[];

    searchResults: DataMartMergeFieldResponse[];
    searchPaginationData: SearchPaginationData | undefined;
    currSearchIteration: number;
}

type SearchPaginationData = {
    searchTerm: string;
    totalRecords: number | undefined;
    batchSize: number | undefined;
};

const initialState: DynamicContentState = {
    mergeFieldTables: [],
    dynamicContent: [],
    libraryContent: [],
    deletedLibraryContent: [],
    mergeFields: [],
    searchResults: [],
    searchPaginationData: undefined,
    currSearchIteration: 0,
};

@Injectable()
export class DynamicContentStore extends ComponentStore<DynamicContentState> {
    readonly #dataMartService = inject(DataMartService);
    readonly #contentAssetService = inject(ContentAssetService);

    dynamicContent$ = this.select(state => state.dynamicContent);
    mergeFieldTables$ = this.select(state => state.mergeFieldTables);
    mergeFieldTables = this.selectSignal(state => state.mergeFieldTables);
    mergeFields$ = this.select(state => state.mergeFields);
    libraryContent = this.selectSignal(state => state.libraryContent);
    availableLibraryContent = computed<ContentAsset[]>(() =>
        this.libraryContent().filter(content => content.enabled && content.valid)
    );
    deletedLibraryContent$ = this.select(state => state.deletedLibraryContent);

    searchResults$ = this.select(state => state.searchResults);
    searchResults = this.selectSignal(state => state.searchResults);
    private searchPaginationData = this.selectSignal(state => state.searchPaginationData);
    private currSearchIteration = this.selectSignal(state => state.currSearchIteration);
    private offset = computed(() => this.currSearchIteration() * (this.searchPaginationData()?.batchSize ?? 0));
    private searchResultFieldMerges = computed(() => this.searchResults().flatMap(result => result.fields));

    constructor() {
        super(initialState);
    }

    reset = this.updater(state => ({
        ...initialState,
    }));
    setMergeFieldTables = this.updater((state, tables: Pick<DataMartTable, 'displayName' | 'name'>[]) => ({
        ...state,
        mergeFieldTables: tables,
    }));
    setMergeFields = this.updater((state, fieldMerges: DataMartMergeField[]) => ({
        ...state,
        mergeFields: fieldMerges,
    }));

    setDynamicContent = this.updater((state, content: DynamicContent[]) => ({
        ...state,
        dynamicContent: content,
    }));

    setLibraryContent = this.updater((state, content: ContentAsset[]) => ({
        ...state,
        deletedLibraryContent: content.filter(asset => asset.deletedAt !== null),
        libraryContent: content.filter(asset => asset.deletedAt === null),
    }));

    setSearchPaginationData = this.updater((state, data: SearchPaginationData) => ({
        ...state,
        searchPaginationData: data,
    }));

    appendSearchResults = this.updater((state, results: DataMartMergeFieldResponse[]) => ({
        ...state,
        searchResults: [...state.searchResults, ...results],
    }));

    incrementSearchIteration = this.updater(state => ({
        ...state,
        currSearchIteration: state.currSearchIteration + 1,
    }));

    resetSearchData = this.updater(state => ({
        ...state,
        currSearchIteration: 0,
        searchResults: [],
        searchPaginationData: undefined,
    }));

    getMergeFields = this.effect((tableName$: Observable<string>) => {
        return tableName$.pipe(
            exhaustMap(tableName => {
                return this.#dataMartService.getMergeFields(tableName).pipe(
                    defaultIfEmpty({ fields: [] as DataMartMergeField[] }),
                    map(res => {
                        return res.fields.map(field => ({ ...field, tableName: tableName }));
                    }),
                    tapResponse({
                        next: fields => {
                            this.setMergeFields(fields);
                        },
                        error: err => {
                            this.setMergeFields([]);
                        },
                    })
                );
            })
        );
    });

    searchMergeFields = this.effect((searchTerm$: Observable<string>) => {
        return searchTerm$.pipe(
            exhaustMap(searchTerm => {
                if (this.searchPaginationData()?.searchTerm === searchTerm) {
                    if (this.searchPaginationData()?.totalRecords! <= this.searchResultFieldMerges().length) {
                        return of(); // don't search
                    } else {
                        return this.doFieldMergeSearch(searchTerm);
                    }
                } else {
                    this.resetSearchData();
                    return this.doFieldMergeSearch(searchTerm);
                }
            })
        );
    });

    private doFieldMergeSearch(searchTerm: string) {
        return this.#dataMartService.searchMergeFields(searchTerm, this.offset()).pipe(
            tapResponse({
                next: res => {
                    this.incrementSearchIteration();
                    this.sortEntriesAndFields(res.entries);
                    this.appendSearchResults(res.entries);
                    this.setSearchPaginationData({
                        searchTerm: searchTerm,
                        totalRecords: res.totalRecords,
                        batchSize: res.returnRecords,
                    });
                },
                error: err => {
                    console.error(err);
                },
            })
        );
    }

    getMergeFieldTables = this.effect(trigger$ => {
        return trigger$.pipe(
            exhaustMap(() => {
                return this.#dataMartService.getMergeFieldTables().pipe(
                    tapResponse({
                        next: res => {
                            this.setMergeFieldTables(res.tables);
                        },
                        error: err => console.error(err),
                    })
                );
            })
        );
    });

    searchContentAssets = rxMutation({
        operation: (search: ContentAssetSearch) => {
            return this.#contentAssetService.search(search);
        },
        onSuccess: res => {
            this.setLibraryContent(res);
        },
        onError: err => {
            console.error(err);
        },
        operator: exhaustOp,
    });

    getDynamicContent = this.effect(trigger$ => {
        return trigger$.pipe(
            exhaustMap(() => {
                return this.#dataMartService
                    .getMergeFieldTables()
                    .pipe(
                        map(mergeFieldResponse => mergeFieldResponse.tables),
                        mapArray(table => {
                            const tfsGroup: FieldMergeGroup = {
                                displayName: table.displayName,
                                value: table.name,
                            };
                            return this.transformToDynamicContent(tfsGroup);
                        })
                    )
                    .pipe(
                        map(tables => {
                            const libraryContentGroup: FieldMergeGroup = {
                                displayName: 'Library Content',
                                value: 'library content',
                            };
                            const libraryContent = this.transformToDynamicContent(libraryContentGroup);
                            return [...tables, libraryContent];
                        }),
                        tapResponse({
                            next: content => {
                                this.setDynamicContent(content);
                            },
                            error: err => console.error(err),
                        })
                    );
            })
        );
    });

    private sortEntriesAndFields(entries: DataMartMergeFieldResponse[]) {
        entries.sort((a, b) => a.tableDisplayName!.localeCompare(b.tableDisplayName!) ?? 0);
        entries.forEach(entry => {
            entry.fields.sort((a, b) => a.displayName!.localeCompare(b.displayName!) ?? 0);
        });
    }

    private transformToDynamicContent(group?: FieldMergeGroup): DynamicContent {
        const dynamicContent: DynamicContent = {
            group: undefined,
            elements: undefined,
        };
        const fieldMergesArray: FieldMerge[] = [];
        dynamicContent.group = group;
        dynamicContent.elements = fieldMergesArray;
        return dynamicContent;
    }
}
